"use client";

import Link from "next/link";
import type { Dictionary } from "@/dictionaries/types";
import type { CollectionsResponse } from "@/features/collections/types";
import { Loading, Pagination, StatePanel } from "@/features/home-v2/ui";
import CollectionsSkeleton from "./CollectionsSkeleton";
import CollectionSpotlight from "./CollectionSpotlight";
import CollectionStackCard from "./CollectionStackCard";
import CollectionsLedger from "./CollectionsLedger";
import { useCollectionResults } from "./useCollectionResults";
import type { CollectionsListing } from "./useCollectionsListing";
import { fullRows } from "@/lib/grid-rows";
type Props = {
  listing: CollectionsListing;
  lang: "fa" | "en";
  dict: Dictionary;
  initialData: CollectionsResponse;
  initialNext?: CollectionsResponse;
  pageSize: number;
  onPage: (page: number) => void;
  /** The grid's live columns (`useGridColumns`, owned by the page so its page size can follow them). */
  grid: {
    ref: (el: HTMLElement | null) => void;
    columns: number;
  };
};

/**
 * What the list area shows, in order of precedence: the skeleton (first load,
 * or a tab / sort / filter / page swapping the set — under a running bar),
 * error, filtered-empty, empty, then the results — the grid (with the podium
 * lifted out on the Top tab's first unfiltered page) or the ledger — with
 * pagination, or the infinite sentinel in a category's grid.
 */
export default function CollectionsResults({
  listing,
  lang,
  dict,
  initialData,
  initialNext,
  pageSize,
  onPage,
  grid
}: Props) {
  // The Top tab's grid lends its first three to the podium; each page then runs on by
  // as many as keep its rows whole (3 mod the columns), so no row is ever short.
  const rankedGrid = listing.tab === "top" && !listing.categoryEndpoint && listing.view === "grid" && !listing.hasActiveFilters;
  // Before the grid is measured (the server's render) it runs on by three — right for every desktop width
  // (4 to 8 columns); the CSS holds back a short row until the measure corrects it.
  const borrow = !rankedGrid ? 0 : grid.columns === 0 ? 3 : grid.columns > 1 ? 3 % grid.columns : 0;
  const r = useCollectionResults(listing, lang, initialData, pageSize, borrow, initialNext);
  const retryButton = <button type="button" className="v2-btn v2-btn--ghost v2-focus" onClick={r.retry}>{dict.states.retry}</button>;
  const skeleton = <CollectionsSkeleton view={listing.view} count={pageSize} label={dict.states.loading} />;
  if (r.isLoading && r.items.length === 0) return skeleton;
  if (r.isSwitching) {
    return <div className="v2-clx-results" aria-busy>
        <span className="v2-clx-progress" aria-hidden><i /></span>
        {skeleton}
      </div>;
  }
  if (r.items.length === 0) {
    if (!r.isSuccess) return <StatePanel alert title={dict.states.errorTitle} body={dict.states.errorBody} action={retryButton} />;
    if (listing.hasActiveFilters) {
      return <StatePanel title={dict.states.filteredEmptyTitle} body={dict.states.filteredEmptyBody} action={<button type="button" className="v2-btn v2-btn--ghost v2-focus" onClick={listing.clearFilters}>{dict.states.clearFilters}</button>} />;
    }
    return <StatePanel title={dict.discovery.collectionsEmptyTitle} body={dict.discovery.collectionsEmptyBody} action={<Link className="v2-btn v2-btn--ghost v2-focus" href={`/${lang}/market`}>{dict.discovery.browseMarket}</Link>} />;
  }
  const ranked = listing.tab === "top" && !listing.categoryEndpoint;
  const podium = ranked && listing.view === "grid" && r.currentPage === 1 && !listing.hasActiveFilters && r.items.length > 3;
  // A category's endless grid shows whole rows until it ends (lib/grid-rows).
  const pageItems = podium ? r.items.slice(3) : r.items;
  // A numbered page that is not the last one shows whole rows only — while the cards it runs on into are
  // still on their way, the row they will complete waits with them instead of drawing short.
  const wholeRows = !r.usesInfinite && grid.columns > 1 && r.currentPage < r.lastPage ? Math.floor(pageItems.length / grid.columns) * grid.columns : pageItems.length;
  const gridItems = r.usesInfinite ? pageItems.slice(0, fullRows(pageItems.length, pageItems.length, grid.columns, !r.infinite.hasMore)) : pageItems.slice(0, wholeRows || pageItems.length);
  const gridFirstRank = podium ? 4 : r.firstRank;
  return <div className="v2-clx-results" aria-busy={r.isFetching || undefined}>
      {r.staleAfterFailure ? <StatePanel alert title={dict.states.errorTitle} body={dict.states.errorBody} action={retryButton} /> : null}

      {listing.view === "grid" ? <>
          {podium ? <CollectionSpotlight items={r.items.slice(0, 3)} lang={lang} /> : null}
          <ul ref={grid.ref} className="v2-clx-grid" data-more={!r.usesInfinite && r.currentPage < r.lastPage ? true : undefined}>
            {gridItems.map((c, i) => <li key={c.id}><CollectionStackCard collection={c} lang={lang} rank={ranked ? gridFirstRank + i : undefined} /></li>)}
          </ul>
        </> : <CollectionsLedger items={r.items} lang={lang} labels={dict.browse} firstRank={r.firstRank} />}

      {r.usesInfinite ? <>
          {r.infinite.hasMore ? <div ref={r.infinite.sentinelRef} className="v2-clx-sentinel">
              {r.infinite.isFetchingMore ? <Loading label={dict.states.loading} /> : <button type="button" className="v2-btn v2-btn--ghost v2-focus" onClick={r.infinite.loadMore}>{dict.browse.loadMore}</button>}
            </div> : null}
          {r.infinite.isError ? <StatePanel alert title={dict.states.errorTitle} body={dict.states.errorBody} action={retryButton} /> : null}
        </> : <Pagination current={r.currentPage} last={r.lastPage} locale={lang} label={dict?.pagination?.label ?? (lang === "fa" ? "صفحه‌بندی" : "Pagination")} buildHref={listing.buildPageHref} onPage={onPage} />}
    </div>;
}